<template>
  <transition name="storybook-page" @enter="handleEnter" @afterEnter="handleAfterEnter" @afterLeave="handleAfterLeave">
    <div class="pa-storybook-page" ref="storyBookPage" v-if="isOpen" @scroll="handleScroll">
      <div class="page-words-field" :class="wordsFieldClass" ref="storyBookPageWords">
        <span v-html="storyHtml"></span>
      </div>
    </div>
  </transition>
</template>

<script>
import {GetStoryBookmark, GetChapterData, GetChapterPages, GetStoryPage, GetChapterList} from '@/graphql/queries/Story.graphql'

import {UpdateBookmark, UpdateMarkAsRead} from '@/graphql/mutations/Story.graphql'
import {GetCharacterItemStore} from '@/graphql/queries/CharacterItemStore.graphql'
import {GetModalAlertInfomations} from '@/graphql/queries/Character.graphql'

export default {
  props : {
    isOpen : {
      default: false
    }
  },
  
  data(){
    return {
      couldReadStoryMetaData  : false,

      readingPercent          : 0,

      wordsFieldClass         : {
        'page-words-field-visible'  : false,
        'page-words-field-hide'     : false
      },

      storyHtml               : null,

      couldSendScrollData     : true,

      selectedPageId          : null,
      selectedChapterIndex    : null,

      scrollSize              : 45,

      scrollPercent           : {
        top     : null,
        bottom  : null
      },
    };
  },

  apollo : {
    bookmark : {
      query : GetStoryBookmark,
      variables(){
        return {
          storyId : this.selectedStoryId,
        };
      },
      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    chapterData : {
      query : GetChapterData,
      variables(){
        return {
          id  : this.selectedChapterId
        }
      },
      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    chapterList : {
      query : GetChapterList,
      variables(){
        return {
          storyId: this.selectedStoryId
        }
      },

      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    chapterPages : {
      query : GetChapterPages,
      variables(){
        return {
          storyChapterId  : this.selectedChapterId
        }
      },
      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    storyPage : {
      query : GetStoryPage,
      variables(){
        return {
          id : this.selectedPageId
        };
      },
      skip(){
        if(this.selectedPageId !== null){
          return false;
        }else{
          return true;
        }
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    characterItemStoreList : {
      query : GetCharacterItemStore,
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    // latestGotCharacterItem: {
    //   query: GetLatestGotCharacterItem
    // },

    modalAlertInfomations : {
      query: GetModalAlertInfomations
    }
  },

  computed : {
    displayHeight(){
      return window.innerHeight;
    },

    selectedStoryId(){
      return this.$store.state.storyBook.selectedStoryId;
    },

    selectedChapterId(){
      return this.$store.state.storyBook.selectedChapterId;
    },

    paginate(){
      return this.$store.state.storyBook.paginate;
    }
  },

  watch : {
    bookmark : {
      handler(data){
        if(typeof data === 'undefined') return;

        this.setPageIndex();
      }
    },

    chapterPages : {
      handler(data){
        if(typeof data === 'undefined') return;
        
        this.setPageIndex();
      },
      immediate: true
    },

    storyPage : {
      handler(data){
        if(typeof data === 'undefined') return;

        this.storyHtml = data.body;
      },
      immediate: true
    },

    storyHtml : {
      handler(value){
        if(value === null) return;

        this.$nextTick(() => {
          this.executeParagraphAnimation();
        });
      },
      immediate: true
    },

    selectedChapterIndex : {
      handler(value){
        if(typeof value === 'undefined') return;
        if(value === null) return;
        if(typeof this.chapterPages === 'undefined') return;
        
        this.selectedPageId = this.chapterPages[value].id;

        this.setNavigatorPaginateParams();
      },
      immediate: true
    },

    scrollPercent : {
      handler(){
        // ここで現在のPercentを送信します。
        this.saveBookmark();
        
        this.detectChapterReaded();
      },
      deep: true
    }
  },

  methods : {
    updateMarkAsRead(){
      return new Promise((resolve) => {
        this.$apollo.mutate({
          mutation  : UpdateMarkAsRead,
          variables : {
            storyId         : this.selectedStoryId,
            storyChapterId  : this.selectedChapterId
          },
          update    : (store, {data: {storyChapter}}) => {
            const parameters = {
              query     : GetChapterData,
              variables : {
                id : this.selectedChapterId
              }
            };

            const readData = store.readQuery(parameters);

            readData.storyChapter = storyChapter;

            parameters.data = readData;

            store.writeQuery(parameters);
          }
        }).then(async () => {
          resolve(true);
        }).catch(async (error) => {
          console.error(error);

          resolve(false);
        });
      });
    },
    
    saveBookmark(){
      return new Promise((resolve) => {
        this.$apollo.mutate({
          mutation  : UpdateBookmark,
          variables : {
            storyId         : this.selectedStoryId,
            storyChapterId  : this.selectedChapterId,
            storyPageId     : this.selectedPageId,
            pagePercent     : this.scrollPercent.top
          }, 
          update    : (store, {data: {bookmark}}) => {
            const parameters = {
              query     : GetStoryBookmark,
              variables : {
                storyId : this.selectedStoryId,
              }
            };

            const readData = store.readQuery(parameters);

            readData.bookmark = bookmark;

            parameters.data = readData;

            store.writeQuery(parameters);
          }
        }).then(async () => {
          resolve(true);
        }).catch(async (error) => {
          console.error(error);

          resolve(false);
        });
      });
    },

    async detectChapterReaded(){
      if(
        (this.scrollPercent.bottom === 1) && 
        (this.paginate.next === false)
      ){
        // ここでチャプターの読了フラグを送信します。
        await this.updateMarkAsRead();

        // 最後にアイテムが増えている可能性があるので、アイテムを更新したい。
        // また、チャプターがアンロックされているので、ロックの解除をします。
        this.$apollo.queries.characterItemStoreList.refetch();
        // this.$apollo.queries.latestGotCharacterItem.refetch();
        this.$apollo.queries.modalAlertInfomations.refetch();
        this.$apollo.queries.chapterList.refetch();
      }
    },

    setNavigatorPaginateParams(){
      if(typeof this.chapterPages === 'undefined') return;

      const parameters = {
        prev        : false,
        next        : false,

        prevPageId  : null,
        nextPageId  : null,

        index       : this.selectedChapterIndex,
        max         : this.chapterPages.length
      };

      if(this.chapterPages.length > (parameters.index + 1)){
        parameters.next       = true;
        parameters.nextPageId = this.chapterPages[(parameters.index + 1)].id;
      }

      if((parameters.index - 1) >= 0){
        parameters.prev       = true;
        parameters.prevPageId = this.chapterPages[(parameters.index - 1)].id;
      }

      this.$store.dispatch({
        type        : 'storyBook/updatePaginate',
        parameters  : parameters
      });
    },

    setPageIndex(){
      if(typeof this.bookmark === 'undefined') return;
      if(typeof this.chapterPages === 'undefined') return;

      let pageIndex = 0;
      this.readingPercent = 0;

      // Bookmarkが存在しない場合は最初のページを指定するが、
      // Bookmarkが存在し、なおかつ指定されたpageIdが表示中のchapter内のページの場合は
      // pageIndexを設定する
      if(this.bookmark !== null){
        for(let i = 0; i < this.chapterPages.length; i++){
          if(this.bookmark.storyPage.id === this.chapterPages[i].id){
            this.readingPercent = this.bookmark.page_percent;

            pageIndex = i;
            break;
          }
        }
      }

      this.selectedChapterIndex = pageIndex;
    },

    handleEnter(){
      this.couldReadStoryMetaData = true;

      this.$refs.storyBookPage.style.height = (this.displayHeight - ((12*2) + 50))+'px';
    },

    handleAfterEnter(){
      this.sendScrollData();
    },

    handleAfterLeave(){
    },

    handleScroll(){
      this.sendScrollData();
    },

    hasReadingPercent(){
      if(this.readingPercent === null) return false;
      if(this.readingPercent === 0) return false;

      return true;
    },

    executeParagraphAnimation(){
      this.wordsFieldClass['page-words-field-hide'] = false;

      if( this.hasReadingPercent() ){
        const scrollHeight     = this.$refs.storyBookPage.scrollHeight;

        const currentScrollTop = this._.floor((scrollHeight * this.readingPercent), 0);

        this.$refs.storyBookPage.scrollTop = currentScrollTop;

        this.wordsFieldClass['page-words-field-visible'] = true;
      }else{
        this.$refs.storyBookPage.scrollTop = 0;

        this.wordsFieldClass['page-words-field-visible'] = false;

        const paragraphList = this.$refs.storyBookPageWords.querySelectorAll('p');
        
        let counter = 0;

        [].forEach.call(paragraphList, (el) => {
          if(counter <= paragraphList.length){
            setTimeout(() => {
              el.classList.add('line-visible');
            }, (200*counter));
          }

          counter += 1;
        });
      }

      // スクロールが発生しない短い項目用にページが表示されたら読了できるようにします。
      this.detectChapterReaded();
    },

    getScrollPercent(){
      if(typeof this.$refs.storyBookPage === 'undefined'){
        return {
          top     : 0,
          bottom  : 0
        }
      }

      // 現在のスクロール位置を取得します。
      const scrollTop     = this.$refs.storyBookPage.scrollTop;
      const scrollHeight  = this.$refs.storyBookPage.scrollHeight;

      // ScrollTopのパーセントです。
      const percent       = this._.floor(((scrollTop / scrollHeight) * 100), 2);
      const dotPercent    = this._.floor((percent / 100), 2);

      // ScrollBottomのパーセントです。
      const percentBottom    = this._.floor((((this.$refs.storyBookPage.clientHeight + scrollTop) / scrollHeight) * 100), 2);
      const dotPercentBottom = this._.floor((percentBottom / 100), 2);

      return {
        top     : dotPercent,
        bottom  : dotPercentBottom,
      }
    },

    sendScrollData(){
      if(this.couldSendScrollData === false) return;
      this.couldSendScrollData = false;

      setTimeout(() => {
        this.couldSendScrollData = true;

        // 現在のスクロール位置をパーセントで取得します。
        this.scrollPercent = this.getScrollPercent();

        // 現在のスクロール位置からスクロールアップ、スクロールダウンできるかのフラグを保存します。
        this.$store.dispatch({
          type        : 'storyBook/updateCouldScroll',
          parameters  : this.getCouldScroll()
        })
      }, 500);
    },

    getCouldScroll(){
      const scrollTopHeight = this.$refs.storyBookPage.scrollHeight - this.$refs.storyBookPage.clientHeight;

      const couldScroll = {
        up    : false,
        down  : false
      }

      if(this.$refs.storyBookPage.scrollTop < scrollTopHeight){
        couldScroll.down = true;
      }else{
        couldScroll.down = false;
      }

      if(this.$refs.storyBookPage.scrollTop === 0){
        couldScroll.up = false;
      }else{
        couldScroll.up = true;
      }

      return couldScroll;
    },

    handleScrollUp(){
      if(typeof this.$refs.storyBookPage === 'undefined') return;

      // scrollTopが0より大きい場合は0になるまでスクロールします。
      if(0 < this.$refs.storyBookPage.scrollTop){
        this.$refs.storyBookPage.scrollTop -= this.scrollSize;
      }else{
        // もしもprevPageが存在する場合はPrevへ移動します。
        if(this.paginate.prev === false) return;

        this.wordsFieldClass['page-words-field-hide'] = true;

        for(let i = 0; i < this.chapterPages.length; i++){
          if(this.paginate.pageId.prev === this.chapterPages[i].id){
            this.readingPercent = 0;
            this.selectedChapterIndex = i;

            break;
          }
        }
      }
    },

    handleScrollDown(){
      if(typeof this.$refs.storyBookPage === 'undefined') return;

      if( !(this.$refs.storyBookPage.scrollHeight <= (this.$refs.storyBookPage.scrollTop + this.$refs.storyBookPage.clientHeight)) ){
        this.$refs.storyBookPage.scrollTop += this.scrollSize;
      }else{
        // もしもnextPageが存在する場合はNextへ移動します。
        if(this.paginate.next === false) return;

        this.wordsFieldClass['page-words-field-hide'] = true;

        for(let i = 0; i < this.chapterPages.length; i++){
          if(this.paginate.pageId.next === this.chapterPages[i].id){
            this.readingPercent = 0;
            this.selectedChapterIndex = i;

            break;
          }
        }
      }
    }
  },
  
  created(){
    this.$parent.$on('pageScrollUp',    this.handleScrollUp);
    this.$parent.$on('pageScrollDown',  this.handleScrollDown);
  }
}
</script>

<style lang="scss">
@import "~bulma/sass/utilities/_all";
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/mixins.scss";

@keyframes storybookTextFade
{
  0%   {
    transform: scale(.99);
    backdrop-filter: blur(3px);
    opacity:0;
  }
  100% {
    transform: scale(1);
    backdrop-filter: blur(0px);
    opacity:1;
  }
}

@keyframes fastFadeOut
{
  0%   {
    top:0;
    opacity:1;
  }
  100% {
    top:0;
    opacity:0;
  }
}

.storybook-page{
  &-enter-active{
    animation: storybookTextFade 2s;
  }

  &-leave-active{
    animation: fastFadeOut 1s;
  }

  &-enter, &-leave-to{
    opacity: 0;
  }
}

.pa-storybook-page{
  height: calc(100vh - (24px + 50px));
  overflow: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling:touch;
  overflow-y: scroll;
  user-select: none;
  padding:.6rem .3rem 0;

  p{
    font-size: 1rem;
    margin-bottom:1rem;
  }

  @media screen and (min-width: $desktop) {
    padding:12px 8px;

    p{
      font-size:1.1rem;
      margin-bottom:12px;
    }
  }

  .page-words-field{
    p{
      opacity:0;

      &.line-visible{
        opacity:1;
        transition: opacity 1.3s ease-in-out;
      }
    }

    &-visible{
      p{
        opacity: 1;
        transition: opacity 0.5s ease-in-out;
      }
    }

    &-hide{
      p{
        opacity:0;
        transition: opacity 0.5s ease-in-out;

        &.line-visible{
          opacity:0;
          transition: opacity 0.5s ease-in-out;
        }
      }
    }
  }
}
</style>